import { forEach, isEmpty, join, map, sumBy } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';

import { ContentZonesService, LocaleService } from '@sm360/widget-commons-core-services';
import { CurrencyFormats, ModelYear, PurchaseMethods, Quote } from '@sm360/widget-commons-vehicle';

import Protection from 'protections/components/ProtectionCategory/Protection/Protection.jsx';

import 'protections/components/ProtectionCategory/ProtectionPackage/ProtectionPackage.scss';

const { PurchaseMethodsService } = PurchaseMethods;

class ProtectionPackage extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            learnMore: false,
        };

        this.learnMore = this.learnMore.bind(this);
    }

    learnMore() {
        this.setState({
            learnMore: !this.state.learnMore,
        });
    }

    render() {
        const {
            isLease,
            selectedFinanceTerm,
            taxesFactor,
            taxCodes,
            protectionCategory,
            protectionPackage,
            protectionPackageItems,
            webUrl,
            selectProtectionPackage,
        } = this.props;

        const protectionPackageItemsLabels = map(protectionPackageItems, (item) => item.label);
        const protectionPackageItemsContentLabels = !isEmpty(protectionPackageItemsLabels)
            ? `(${join(protectionPackageItemsLabels, ', ')})`
            : '';

        const packageOnDemand = LocaleService.t('PROTECTIONS.PRICE_ON_DEMAND');
        let packageFormattedAmount = '';
        let packageFormattedAmountDecimals = '';
        let packageFormattedAmountTaxes = '';
        let packageFormattedAmountTotal = '';

        if (selectedFinanceTerm) {
            let { payment, paymentTaxes } = protectionPackage.calculateItemAmounts(
                isLease,
                selectedFinanceTerm,
                taxesFactor
            );
            forEach(protectionPackageItems, (item) => {
                const itemAmounts = item.calculateItemAmounts(
                    isLease,
                    selectedFinanceTerm,
                    taxesFactor,
                    protectionPackage
                );
                payment += itemAmounts.payment;
                paymentTaxes += itemAmounts.paymentTaxes;
            });

            if (payment > 0) {
                const splitAmount = PurchaseMethodsService.getFormattedCurrencySplit(
                    LocaleService.l(payment, CurrencyFormats.CURRENCY_2)
                );

                packageFormattedAmount = splitAmount.beforeDecimal;
                packageFormattedAmountDecimals = splitAmount.afterDecimal;
                packageFormattedAmountTaxes = `${LocaleService.l(
                    paymentTaxes,
                    CurrencyFormats.CURRENCY_2
                )} (${taxCodes})`;
                packageFormattedAmountTotal = LocaleService.l(payment + paymentTaxes, CurrencyFormats.CURRENCY_2);
            }
        } else {
            let { retail } = protectionPackage.calculateItemAmounts(isLease, selectedFinanceTerm, taxesFactor);
            retail += sumBy(
                protectionPackageItems,
                (item) => item.calculateItemAmounts(isLease, selectedFinanceTerm, taxesFactor, protectionPackage).retail
            );
            if (retail > 0) {
                packageFormattedAmount = LocaleService.l(retail, CurrencyFormats.CURRENCY_0);
            }
        }

        const protectionLearnText = this.state.learnMore
            ? LocaleService.t('PROTECTIONS.LEARN_LESS')
            : LocaleService.t('PROTECTIONS.LEARN_MORE');

        let items = null;
        if (this.state.learnMore) {
            items = !isEmpty(protectionPackageItems) ? (
                protectionPackageItems.map((protection) => {
                    return <Protection key={protection.selectionCode} protection={protection} />;
                })
            ) : (
                <Protection key={protectionPackage.selectionCode} protection={protectionPackage} />
            );
        }

        const brochure = this.state.learnMore &&
            webUrl && (
                <a
                    href={protectionCategory.webUrl}
                    className="brochure is-util"
                    rel="noopener noreferrer"
                    target="_blank"
                >
                    <icon className="icon-brochure" /> {LocaleService.t('PROTECTIONS.VIEW_BROCHURE')}
                </a>
            );

        const selectClass = protectionPackage.selected
            ? 'protection-cta-selector selected'
            : 'protection-cta-selector is-util';

        const selectLabel = protectionPackage.selected
            ? LocaleService.t('PROTECTIONS.SELECTED')
            : LocaleService.t('PROTECTIONS.SELECT');

        const widgetname = 'widget.protections';
        let czPackagePopular = ContentZonesService.getContentZone(widgetname, `${widgetname}.most.popular.tag`);
        czPackagePopular = czPackagePopular ? czPackagePopular.getValue() : '';

        return (
            <div className="protection-single--content">
                {protectionPackage.mostPopular && (
                    <img src={czPackagePopular} className="protection-single--popular" alt="Popular" />
                )}
                <div className="protection-intro">
                    <div className="protection-text">
                        <h4 className="sr-protection-package-title">{protectionPackage.label}</h4>
                        <div className="sr-protection-package-descrition">{protectionPackage.description}</div>
                        <div className="sr-protection-package-descrition">{protectionPackageItemsContentLabels}</div>
                        <button className="sr-link" onClick={this.learnMore} type="button">
                            {protectionLearnText}
                        </button>
                    </div>
                    <div className="protection-price">
                        <span className="is-price">{packageFormattedAmount}</span>
                        <sup className="is-price">{packageFormattedAmountDecimals}</sup>
                        {packageFormattedAmountTaxes && (
                            <span className="protection-price--details">
                                {' '}
                                + {packageFormattedAmountTaxes} = {packageFormattedAmountTotal}
                            </span>
                        )}
                        {!packageFormattedAmount && (
                            <span className="protection-price--details">{packageOnDemand}</span>
                        )}
                    </div>
                    <div className="protection-buttons">
                        <button
                            className={selectClass}
                            onClick={() => selectProtectionPackage(protectionCategory, protectionPackage)}
                            type="button"
                        >
                            <icon className="icon-Crochet" />
                            {selectLabel}
                        </button>
                    </div>
                </div>
                <div className="protection-content sr-protection-package-descrition">
                    {items}
                    {brochure}
                </div>
            </div>
        );
    }
}

ProtectionPackage.propTypes = {
    isLease: PropTypes.bool.isRequired,
    protectionCategory: PropTypes.instanceOf(Quote.QuoteItemCategoryModel).isRequired,
    protectionPackage: PropTypes.instanceOf(ModelYear.OptionModel).isRequired,
    protectionPackageItems: PropTypes.arrayOf(PropTypes.instanceOf(ModelYear.OptionModel)).isRequired,
    selectedFinanceTerm: PropTypes.instanceOf(PurchaseMethods.TermModel),
    selectProtectionPackage: PropTypes.func.isRequired,
    taxCodes: PropTypes.string.isRequired,
    taxesFactor: PropTypes.number.isRequired,
    webUrl: PropTypes.string.isRequired,
};

ProtectionPackage.displayName = 'ProtectionPackage';

export default ProtectionPackage;



// WEBPACK FOOTER //
// ./src/components/ProtectionCategory/ProtectionPackage/ProtectionPackage.jsx